<template>
    <div class="skadestatistik">
        <KHeader heading="Skadestatistik" />
        <KFilter
            name="statistik-filter"
            :schema="filterSchema"
            :defaultValue="defaultFilterValues"
            @submit="onFilter"
            expanded
            hide-search
        />

        <ButtonGroup>
            <Button type="button" role="button" size="secondary" @click.prevent="startDownloadExcelJob()">
                <img src="@/assets/download_white.svg" />Exportera egna skador till excel
            </Button>
            <span v-if="fetchExcelError">Fel vid hämtning av excel: {{ fetchExcelError }}</span>
        </ButtonGroup>

        <IdrottPopup v-if="idrotter.length > 1" variant="statistik" @chooseIdrott="chooseIdrott" />

        <div v-if="getLokalisationDataObject.chartData">
            <!-- height: 5px per raw per label/etikett/alternativ-->
            <statistik-component
                chartAxis="y"
                :width="200"
                :height="chartHeightLokalisation"
                :chartData="getLokalisationDataObject"
            />
            <statistik-component
                chartAxis="y"
                :width="200"
                :height="chartHeightSkademekanism"
                :chartData="getSkademekanismDataObject"
            />
            <statistik-component
                chartAxis="y"
                :width="200"
                :height="chartHeightSkadetyp"
                :chartData="getSkadetypDataObject"
            />
        </div>
    </div>
</template>

<script>
    import StatistikComponent from "./StatistikComponent.vue";
    import KFilter from "@/components/KFilter/KFilter.vue";
    import KHeader from "@/components/KHeader.vue";
    import IdrottPopup from "@/tabs/Skreg/Statistik/IdrottPopup.vue";
    import { generateSchema } from "@/components/forms/Form/schemaHelpers";
    import { mapActions, mapGetters } from "vuex";
    import moment from "moment";
    import BaseTab from "@/tabs/mixins/BaseTab";
    import tabIcon from "@/assets/narvarostatistik.svg";
    import { getConfig, klinikenApi } from "@/api";
    import { startAsyncTask } from "@/utils/asyncTaskPoller";
    import { cloneDeep } from "lodash";
    import Button from "@/components/Button.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";

    export default {
        extends: {
            BaseTab,
        },
        name: "Skadestatistik",
        tabName: "Skadestatistik",
        icon: tabIcon,
        components: {
            StatistikComponent,
            IdrottPopup,
            Button,
            ButtonGroup,
            KFilter,
            KHeader,
        },
        props: {
            tabId: Number,
        },
        data() {
            return {
                fetchExcelError: null,
                idrotter: [],
                filterValues: {
                    from: null,
                    to: null,
                    liga: "Alla ligor",
                },
            };
        },
        computed: {
            ...mapGetters("systemData", ["kundNamn"]),
            //chart-getters per datatyp
            ...mapGetters("skreg/statistik", [
                "getSkademekanismDataObject",
                "getLokalisationDataObject",
                "getSkadetypDataObject",
                "getIdrotter",
                "getLigor",
                "getIdrott",
                "getLiga",
            ]),

            filterSchema() {
                return generateSchema({
                    addToAll: { ignoreEmptyData: true },
                    schema: [
                        {
                            component: "KAutocomplete",
                            name: "liga",
                            label: "Liga",
                            options: [
                                { value: "Alla ligor", label: "Alla ligor" },
                                ...(this.getLigor ?? []).map((l) =>
                                    typeof l === "object" ? l : { value: l, label: l }
                                ),
                            ],
                            autoPosition: true,
                            validation: "required",
                        },
                        { component: "KDateTimepicker", name: "from", label: "Fr.o.m", validation: "required" },
                        { component: "KDateTimepicker", name: "to", label: "T.o.m", validation: "required" },
                    ],
                });
            },
            defaultFilterValues() {
                const firstDate = moment().startOf("month");
                const lastDate = moment().endOf("month");
                return {
                    liga: "Alla ligor",
                    from: firstDate.format("YYYY-MM-DD"),
                    to: lastDate.format("YYYY-MM-DD"),
                };
            },
            lengthLabelsSkadetyp() {
                return this.$store.state.skreg.statistik.skadetypDataObject.chartData.labels.length;
            },
            lengthLabelsSkademekanism() {
                return this.$store.state.skreg.statistik.skademekanismDataObject.chartData.labels.length;
            },
            lengthLabelsLokalisation() {
                return this.$store.state.skreg.statistik.lokalisationDataObject.chartData.labels.length;
            },
            // 10 px per label för de flesta charts, 15 px för skadetyp (mer text per rad)
            chartHeightLokalisation() {
                return 10 * this.lengthLabelsLokalisation;
            },
            chartHeightSkademekanism() {
                return 10 * this.lengthLabelsSkademekanism;
            },
            chartHeightSkadetyp() {
                return 15 * this.lengthLabelsSkadetyp;
            },
        },
        methods: {
            ...mapActions("skreg/statistik", [
                "fetchSkadekort",
                "fetchIdrotter",
                "fetchLigor",
                "setIdrott",
                "clearStatistik",
            ]),

            onFilter({ formData }) {
                this.filterValues = { ...formData };
                this.initialize();
            },

            async initialize() {
                let _chartopt = cloneDeep(this.dataSetsOptions());
                await this.$store.dispatch("tabs/displaySpinner", {
                    id: this.tabId,
                    display: true,
                    ignoreTimeout: true,
                });
                try {
                    await this.fetchSkadekort({
                        from: this.filterValues.from,
                        to: this.filterValues.to,
                        idrott: this.getIdrott,
                        liga: this.filterValues.liga || "Alla ligor",
                        chartOptions: _chartopt,
                    });
                    setTimeout(() => {
                        this.$emit("updateTable");
                    }, 1000);
                } finally {
                    if (this.$el !== undefined) {
                        await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });
                    }
                }
            },

            async startDownloadExcelJob() {
                const baseUrl = `skreg/statistik/idrotter/${this.getIdrott}/export-skadedata-excel/`;
                const excelMimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

                const { promise, cancel } = startAsyncTask(baseUrl, (taskId) => `${baseUrl}?task_id=${taskId}`, {
                    maxWaitMs: 600_000,
                    postConfig: { params: { startDate: "", endDate: "", liga: "" } },
                    fetchPollResult: async (taskId) => {
                        const response = await klinikenApi.get(
                            `${baseUrl}?task_id=${taskId}`,
                            getConfig({ responseType: "blob" })
                        );
                        const text = await new Blob([response.data]).text();
                        if (text === '"Inget resultat tillgängligt"') return undefined;
                        return new Blob([response.data], { type: excelMimeType });
                    },
                });
                this._cancelExcelTask = cancel;

                await this.$store.dispatch("tabs/displaySpinner", {
                    id: this.tabId,
                    display: true,
                    ignoreTimeout: true,
                });

                let taskResult;
                try {
                    taskResult = await promise;
                } catch (error) {
                    if (error?.type !== "cancelled") {
                        this.fetchExcelError =
                            error?.type === "task_failure"
                                ? error.exc_message
                                : error?.type === "timeout"
                                  ? "Exporten tog för lång tid. Försök igen."
                                  : "Ett fel uppstod vid export.";
                    }
                    await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });
                    return;
                } finally {
                    this._cancelExcelTask = null;
                }

                await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(taskResult);
                const date = new Date();
                link.download =
                    "skadestatistik_" +
                    String(date.getFullYear()).padStart(2, "0") +
                    String(1 + date.getMonth()).padStart(2, "0") +
                    String(date.getDate()).padStart(2, "0") +
                    String(date.getHours()).padStart(2, "0") +
                    String(date.getMinutes()).padStart(2, "0") +
                    ".xlsx";
                link.click();
                URL.revokeObjectURL(link.href);
            },

            dataSetsOptions() {
                const datasets = [
                    {
                        label: `${this.kundNamn} Match`,
                        backgroundColor: "rgba(255, 99, 132, 0.6)",
                        borderWidth: 1,
                        borderColor: "rgba(255,99,132,1)",
                        pointBorderColor: "#2554FF",
                        stack: "Stack 0",
                    },
                    {
                        label: `${this.kundNamn} Träning`,
                        backgroundColor: "rgba(255, 69, 102, 0.2)",
                        //backgroundColor: "--background-color",
                        borderWidth: 1,
                        borderColor: "rgba(255,69,102,1)",
                        pointBorderColor: "#2554FF",
                        stack: "Stack 0",
                    },
                    {
                        label: `${this.filterValues.liga} Match`,
                        backgroundColor: "rgba(132, 99, 255, 0.6)",
                        borderWidth: 1,
                        borderColor: "rgba(132,99,255,1)",
                        pointBorderColor: "#2554FF",
                        stack: "Stack 1",
                    },
                    {
                        label: `${this.filterValues.liga} Träning`,
                        backgroundColor: "rgba(102, 69, 255, 0.2)",
                        borderWidth: 1,
                        borderColor: "rgba(102,69,255,1)",
                        pointBorderColor: "#2554FF",
                        stack: "Stack 1",
                    },
                ];
                return datasets;
            },

            async chooseIdrott(idrott) {
                this.setIdrott(idrott);
                await this.fetchLigor(idrott);
            },
        },

        async created() {
            await this.fetchIdrotter();
            this.idrotter = (this.getIdrotter ?? []).map((i) =>
                typeof i === "object" ? i : { value: i, label: i.charAt(0).toUpperCase() + i.slice(1) }
            );
            if (this.getIdrotter.length === 1) {
                this.setIdrott(this.getIdrotter[0]);
                await this.fetchLigor(this.getIdrott);
            }
        },
        beforeDestroy() {
            this._cancelExcelTask?.();
            this.clearStatistik();
        },
    };
</script>

<style scoped lang="scss">
    @import "@/style/_variables.scss";
    @import "@/style/_deprecated_main.scss";

    .skadestatistik {
        .table {
            .buttonDiv {
                display: flex;
            }

            .visited {
                text-decoration: line-through;
            }
        }

        .downloadButton {
            display: flex;
            justify-content: flex-end;

            button {
                background-color: $color-primary;
                color: #ffffff;
                border: none;
                border-radius: 5px;
                align-self: right;
                padding: 15px;
                margin-left: 10px;
                margin-top: 10px;
            }

            .choosedDatesDownloadButton button {
                background-color: $color-thead;
                color: #ffffff;
            }
        }

        .fade-enter-active,
        .fade-leave-active {
            transition: opacity 1s;
        }

        .fade-enter,
    .fade-leave-to

        /* .fade-leave-active below version 2.1.8 */ {
            opacity: 0;
        }

        .slot {
            section {
                margin-bottom: 30px;
            }
        }
    }
</style>
